// ChartComponent.jsx  – web version aligned with React-Native behaviour
import React, { useState, useEffect, useContext } from "react";
import { ref, get } from "firebase/database";
import {
  collection, query, orderBy, getDoc, doc, limit, getDocs
} from "firebase/firestore";
import { IoFilter } from "react-icons/io5";
import { database, firestore } from "./firebase";
import { AppContext } from './AppContext';

/* ───────── layout helpers ───────── */
const FRAME_WIDTH = 480;
const WIDTH       = Math.min(Math.max(window.innerWidth, 320), FRAME_WIDTH);
const HEIGHT      = Math.min(Math.max(window.innerHeight, 640), 920);

const chartHeight      = 220;
const SVG_WIDTH        = WIDTH - 20;              // always <= 460
const PADDING          = 20;
const EFFECTIVE_WIDTH  = SVG_WIDTH - 2 * PADDING;
const EFFECTIVE_HEIGHT = chartHeight - 2 * PADDING;

/* ───────── date helpers (same as before) ───────── */
const monthAbbr = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const fmtDate   = d=>`${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,"0")}-${String(d.getUTCDate()).padStart(2,"0")}`;
const pastDays  = n=>[...Array(n)].map((_,i)=>{const d=new Date();d.setUTCDate(d.getUTCDate()-(n-1-i));return fmtDate(d);});
const isoWeek = (dStr) => {
    const t = new Date(dStr);                         // always Date, never string
    const n = (t.getUTCDay() + 6) % 7;                // 0 → Mon … 6 → Sun
    t.setUTCDate(t.getUTCDate() - n + 3);             // Thu of this ISO-week
    const th = t.valueOf();
    t.setUTCMonth(0, 1);                              // go to 1 Jan
    if (t.getUTCDay() !== 4) {                        // first Thu of year
      t.setUTCMonth(0, 1 + ((4 - t.getUTCDay()) + 7) % 7);
    }
    return 1 + Math.ceil((th - t) / 604_800_000);     // ms per week
  };
const pastWeeks = n=>[...Array(n)].map((_,i)=>{const d=new Date();d.setUTCDate(d.getUTCDate()-(n-1-i)*7);return `${d.getUTCFullYear()}-W${String(isoWeek(d)).padStart(2,"0")}`;});
const pastMonths=n=>[...Array(n)].map((_,i)=>{const d=new Date();d.setUTCMonth(d.getUTCMonth()-(n-1-i),1);return `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,"0")}`;});

/* build an SVG path out of data */
const genPath = data=>{
  if(!data.length) return "";
  const maxVal=Math.max(...data);
  return data.map((d,i)=>{
    const x=PADDING + (i/(data.length-1))*EFFECTIVE_WIDTH;
    const y=PADDING + (EFFECTIVE_HEIGHT-(d/(maxVal||1))*EFFECTIVE_HEIGHT);
    return `${i===0?"M":"L"} ${x} ${y}`;
  }).join(" ");
};


function normalize(title) {
  return title
    .toLowerCase()
    .replace(/\(.*?\)|\[.*?]|\s*-\s*.*$/g, "")      // drop (…) […] or “- xxxx”
    .split(/\bft\.?|\bfeat\.?/)[0]                  // drop at “ft.” / “feat.”
    .trim()
    .replace(/\s+/g, " ");                          // single-space
}


// ───────────────────────── helpers to probe a month doc
function findSongInDoc(docData, normTitle) {
     return Object.entries(docData.songs || {})
       .find(([title]) => normalize(title) === normTitle);
   }

/* pick the newest (daily/weekly/monthly) keys that exist for this song,
   returning { latestDailyKeys: [...], latestWeeklyKeys: [...], monthlyUsd }      */
   function extractLatestKeys(monthDocs, normTitle) {
  /* monthDocs must be sorted newest → oldest */
  const daily   = [];
    const weekly  = [];
    let monthlyUsd = 0;
  
    /* walk **every** doc, newest → oldest */
    for (const md of monthDocs) {
      const song = findSongInDoc(md, normTitle);
      if (!song) continue;
  
      /* merge keys we find */
      daily .push(...Object.keys(song[1].daily  || {}));
      weekly.push(...Object.keys(song[1].weekly || {}));
  
      /* pick up the first non-zero monthly figure (newest takes precedence) */
      if (!monthlyUsd && song[1].monthly) monthlyUsd = song[1].monthly;
    }
  
    daily .sort();          // ascending
    weekly.sort();
  
    return {
      latestDailyKeys  : daily .slice(-7),  // newest 7 days we’ve found
      latestWeeklyKeys : weekly.slice(-4),  // newest 4 ISO-weeks
      monthlyUsd
    };
}



// Fetch & cache one month’s Firestore document
async function fetchMonth(monthKey, earningsCache, setEarningsCache) {
  if (earningsCache[monthKey]) {
    return earningsCache[monthKey];
  }
  const snap = await getDoc(doc(firestore, "songEarningsSummary", monthKey));
  const data = snap.exists() ? snap.data() : { songs: {} };
  setEarningsCache((prev) => ({ ...prev, [monthKey]: data }));
  return data;
}


/* ───────────────────────────────── MAIN COMPONENT ───────────────────────────────── */
export default function WalletChart({ assetId, songName, royaltyPercentage }) {
const {
  assetsSummary, earningsCache, setEarningsCache
} = useContext(AppContext);

const normSongName = React.useMemo(
  () => normalize(songName || ""),
  [songName]
);

  /* --------------- state --------------- */
  const [viewMode,setViewMode]      = useState("stats");
  const [dataType,setDataType]      = useState("streams");
  const [timeframe,setTimeframe]    = useState("3m");
  const [platform,setPlatform]      = useState("all"); 

  const [labels,setLabels]          = useState([]); 
  const [rawData,setRawData]        = useState([]);
  const [summary,setSummary]        = useState(null);

  const [hoverIdx,setHoverIdx]      = useState(null);
  const [hoverY ,setHoverY ]        = useState(null);

  const reqIdRef = React.useRef(0);


  // fresh slate whenever any control changes
useEffect(() => {
  setRawData([]);
  setLabels([]);
}, [viewMode, dataType, timeframe, platform, assetId]);


/* ─── preload newest 3 month earnings docs at mount ───────────── */
useEffect(() => {
  const months = pastMonths(3);
  (async () => {
    const arr = await Promise.all(
      months.map(mk => fetchMonth(mk, earningsCache, setEarningsCache))
    );
    console.log("📦 pre-fetched songEarningsSummary →", months);
    arr.forEach((doc,i)=>console.log(`   └─ ${months[i]} has`,
                                      Object.keys(doc.songs||{}).length,
                                      "songs`"));
  })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);            // run once

const DEBUG = true;
  /* --------------- first fetch of analyticsSummary --------------- */
/* --------------- first fetch of analyticsSummary --------------- */
useEffect(() => {
  (async () => {
    try {
      const snap = await getDoc(
        doc(firestore, "analyticsSummaries", "summary_1")
      );                                  // ← always pull the master doc
      if (!snap.exists()) {
        console.warn("⚠️ analyticsSummaries/summary_1 not found");
        setSummary({});
        return;
      }
      console.log(
        "📈 loaded analyticsSummaries/summary_1 with",
        Object.keys(snap.data() || {}).length,
        "assets"
      );
      setSummary(snap.data());
    } catch (e) {
      console.error("🔥 analytics summary fetch failed", e);
      setSummary({});
    }
  })();
}, []);


  const getRevenue = (date, bucket) => {
        if (!Array.isArray(assetsSummary) || !assetsSummary.length) return 0;
    
        const asset = assetsSummary.find(a => a.id === assetId);
        if (!asset) return 0;
    
        return asset.editions.reduce(
          (sum, ed) => sum + Number(ed.earnings?.[bucket]?.[date] || 0),
          0
        );
      };


      // ─── on-the-fly aggregators ──────────────────────────────────────────
      const valOf = (v) =>
          typeof v === "number"
            ? v
            : v && typeof v === "object"
                ? Number(v.streams || 0) + Number(v.downloads || 0)
                : 0;
        
        const sumMonth = (obj, yyyyMM) =>
          Object.entries(obj).reduce(
            (s, [k, v]) => k.startsWith(yyyyMM) ? s + valOf(v) : s, 0);
        
        const sumIsoWeek = (obj, yyyyWw) =>
          Object.entries(obj).reduce((s, [k, v]) => {
            if (!/^\d{4}-\d{2}-\d{2}$/.test(k)) return s;
            const w = `${k.slice(0, 4)}-W${String(isoWeek(k)).padStart(2, "0")}`;
            return w === yyyyWw ? s + valOf(v) : s;
          }, 0);

  

  /* --------------- helpers --------------- */
  const getValForDate = (data, id, plat, date, metric = "streams") => {
    const get0 = x => Number(x || 0);
  
    const meta     = Array.isArray(assetsSummary)
                        ? assetsSummary.find(a => a.id === id)
                        : null;
     const tryKeys  = [ id, meta?.contentId ].filter(Boolean);
     const trueKey = tryKeys.find(k => data[k]);
      if (!trueKey && !["spndat", "all"].includes(plat)) return 0;

  
    /* ---------- SALES / TRADES ------------------------------------ */
    if (metric !== "streams") {
      if (metric === "sales") {
        const bucket =
          timeframe === "week"  ? "daily"  :
          timeframe === "month" ? "weekly" : "monthly";
        return getRevenue(date, bucket);
      }
      return 0;
    }
  
    /* ---------- STREAMS: SPNDAT only ------------------------------- */
    if (plat === "spndat") {
      if (!spndatStats) return 0;
      const bucket = /^\d{4}-\d{2}-\d{2}$/.test(date)
        ? "daily"
        : date.includes("W")
        ? "weekly"
        : "monthly";
      return get0(spndatStats[bucket]?.[date]);
    } 
  
    /* ---------- STREAMS: DSP (or “all”) ---------------------------- */
    const asset   = trueKey ? data[trueKey] : {};
    const amazon  = asset.amazon_music?.dates || {};
    const apple   = asset.apple_music?.dates  || {};
    const spotify = asset.spotify?.dates      || {};
  
if (plat === "amazon")
  return date.length === 7
    ? sumMonth(amazon, date)
    : date.includes("W")
        ? sumIsoWeek(amazon, date)
        : get0(amazon[date]);

if (plat === "spotify")
  return date.length === 7
    ? sumMonth(spotify, date)
    : date.includes("W")
        ? sumIsoWeek(spotify, date)
        : get0(spotify[date]);

        if (plat === "apple")
          return date.length === 7
            ? sumMonth(apple, date)
            : date.includes("W")
                ? sumIsoWeek(apple, date)
                : get0(apple[date]?.streams) + get0(apple[date]?.downloads);
        

  
    /* “all”  – add SPNDAT too so everything lines up */
/* “all”  – add SPNDAT too so everything lines up */
const isDay   = /^\d{4}-\d{2}-\d{2}$/.test(date);
const bucket  = isDay ? "daily" : date.includes("W") ? "weekly" : "monthly";
const spndCnt = spndatStats ? spndatStats[bucket]?.[date] || 0 : 0;

const aVal = date.length===7      ? sumMonth (amazon , date)
           : date.includes("W")   ? sumIsoWeek(amazon , date)
           : get0(amazon[date]);

const apVal= date.length===7      ? sumMonth (apple  , date)
           : date.includes("W")   ? sumIsoWeek(apple  , date)
           : get0(apple[date]?.streams)+get0(apple[date]?.downloads);

const sVal = date.length===7      ? sumMonth (spotify, date)
           : date.includes("W")   ? sumIsoWeek(spotify, date)
           : get0(spotify[date]);

const total= aVal + apVal + sVal + spndCnt;

if (DEBUG && total) {
  console.log(
    `📊 ${date}  amazon:${aVal}  apple:${apVal}  spotify:${sVal}  spndat:${spndCnt}  → total:${total}`
  );
}



  
return total; 
  };
  
  
  const fmtRange=(a,b)=>{
    const sd=new Date(a);const ed=new Date(b);
    const s=`${monthAbbr[sd.getMonth()]} ${sd.getDate()}`;
    const e=`${monthAbbr[ed.getMonth()]} ${ed.getDate()}`;
    return sd.getMonth()===ed.getMonth()?`${s}-${ed.getDate()}`:`${s} - ${e}`;
  };

  // --- Realtime DB helper for SPNDAT only -----------------------------
/* ---- Realtime DB helper : always have SPNDAT numbers handy ---- */
const [spndatStats, setSpndatStats] = useState(null);




useEffect(() => {
  if (!assetId) return;

  (async () => {
    try {
      const root  = `streamStats/${assetId}`;
      const [d, w, m] = await Promise.all([
        get(ref(database, `${root}/daily`)),
        get(ref(database, `${root}/weekly`)),
        get(ref(database, `${root}/monthly`))
      ]);

      setSpndatStats({
        daily   : d.exists() ? d.val() : {},
        weekly  : w.exists() ? w.val() : {},
        monthly : m.exists() ? m.val() : {}
      });
    } catch (e) {
      console.error("SPNDAT fetch failed", e);
      setSpndatStats({ daily:{}, weekly:{}, monthly:{} });
    }
  })();
}, [assetId]);



useEffect(() => {
  if (!summary)             return;

  let keys = timeframe === "week"  ? pastDays(7)
           : timeframe === "month" ? pastWeeks(4)
           :                         pastMonths(3);

/* last-active anchoring */
if (timeframe === "week") {
  const days180 = pastDays(180);                      // ~6 months history
  const lastDay = [...days180].reverse()
    .find(d => getValForDate(summary, assetId, platform, d, dataType) > 0);

  if (lastDay) {
    // build 7 consecutive UTC days ending with `lastDay`
    keys = [...Array(7)].map((_, i) => {
      const d = new Date(lastDay);
      d.setUTCDate(d.getUTCDate() - (6 - i));
      return fmtDate(d);
    });
  }
}

if (timeframe === "month") {
  const weeks12 = pastWeeks(12);                    // 12 = plenty of history
  const lastWeek = [...weeks12].reverse()
    .find(w => getValForDate(summary, assetId, platform, w, dataType) > 0);
  if (lastWeek) {
    const idx = weeks12.indexOf(lastWeek);
    keys = weeks12.slice(Math.max(0, idx - 3), idx + 1);   // newest 4 ISO-weeks
  }
}

  setLabels(keys);
  setRawData(keys.map(d =>
    getValForDate(summary, assetId, platform, d, dataType)
  ));
}, [viewMode, summary, assetId, platform, timeframe, dataType]);


// ─── Earnings mode (Streams): DSP USD + SPNDAT USD @ $0.10/stream ───
// ─── Earnings – STREAMS (USD)  ─────────────────────────────────────────
useEffect(() => {
  if (viewMode !== "earnings" || dataType !== "streams") return;

  /* month helpers — keep two orders:
       last3Chron : oldest → newest  (for x-axis)
       docMonths  : newest → oldest  (for Firestore fetch)                */
  const last3Chron = pastMonths(3);          // e.g. ['2025-03','2025-04','2025-05']
  const docMonths  = [...last3Chron].reverse();
  setLabels([]);                             // clear until we rebuild

  const myReq = ++reqIdRef.current;          // race-guard

  (async () => {
    const docs = await Promise.all(
      docMonths.map(mk => fetchMonth(mk, earningsCache, setEarningsCache))
    );

    const { latestDailyKeys, latestWeeklyKeys } =
      extractLatestKeys(docs, normSongName);

    let keys = [], bucket = "";
    if (timeframe === "week")  { keys = latestDailyKeys;  bucket = "daily";  }
    if (timeframe === "month") { keys = latestWeeklyKeys; bucket = "weekly"; }
    if (timeframe === "3m")    { keys = last3Chron;       bucket = "monthly";}

    if (!keys.length) {
      if (reqIdRef.current === myReq) setRawData([]);
      return;
    }

    const usdValues = keys.map(k => {
      /* ---------- DSP USD ---------- */
      const dspUsd = docs.reduce((sum, md, idx) => {
        const monthKey = docMonths[idx];          // matches fetched doc
        const songEnt  = findSongInDoc(md, normSongName);
        if (!songEnt) return sum;
        const data = songEnt[1];

        if (bucket === "daily")   return sum + Number(data.daily?.[k]   || 0);
        if (bucket === "weekly")  return sum + Number(data.weekly?.[k]  || 0);
        if (bucket === "monthly") return monthKey === k
                                   ? sum + Number(data.monthly || 0)
                                   : sum;
        return sum;
      }, 0);

      /* ---------- SPNDAT USD ---------- */
      const isDay  = /^\d{4}-\d{2}-\d{2}$/.test(k);
      const spBuck = isDay ? "daily" : k.includes("W") ? "weekly" : "monthly";
      const spCnt  = Number(spndatStats?.[spBuck]?.[k] || 0);

      return dspUsd + spCnt * 0.10;
    });

    if (reqIdRef.current === myReq) {
      setLabels(keys);
      setRawData(usdValues);
    }
  })();
}, [
  viewMode,
  dataType,
  timeframe,
  normSongName,          // <-- make sure this is in the deps
  earningsCache,
  setEarningsCache,
  spndatStats
]);




// ─── Earnings – SALES (USD)  ───
useEffect(() => {
  if (viewMode !== "earnings" || dataType !== "sales") return;

  let keys, bucket;
  if (timeframe === "week")  { keys = pastDays(7);  bucket = "daily";  }
  if (timeframe === "month") { keys = pastWeeks(4); bucket = "weekly"; }
  if (timeframe === "3m")    { keys = pastMonths(3);bucket = "monthly";}
  setLabels(keys);
  setRawData(keys.map(k => getRevenue(k, bucket)));
}, [viewMode, dataType, timeframe, assetId, assetsSummary]);




  /* --------------- processed values & svg path --------------- */
  /* --------------- processed values & svg path --------------- */
  const processed = viewMode === "stats"
    ? rawData
    : rawData.map(v => {
        if (dataType === "streams") {
          // v already in USD (DSP USD + SPNDAT_count * $0.10)
          return v * (royaltyPercentage / 100);
        }
        if (dataType === "sales") {
          return v * (royaltyPercentage / 100);
        }
        return v;
      });


  const linePath = genPath(processed);
  const fillPath = linePath
    ? `${linePath} L ${SVG_WIDTH-PADDING} ${chartHeight-PADDING} L ${PADDING} ${chartHeight-PADDING} Z`
    : "";
  const maxVal   = processed.length ? Math.max(...processed) : 1;

  /* --------------- x-axis fixed ticks --------------- */
  const xTicks = labels.length
    ? labels.length===1 ? [labels[0]]
      : [labels[0], labels[Math.floor(labels.length/2)], labels[labels.length-1]]
    : [];

  /* --------------- formatter --------------- */
  const fmtVal = (v = 0) => {
    if(viewMode==="stats" && dataType==="streams") return Number.isInteger(v)?String(v):v.toFixed(1);
    if(v===0) return "0.00";
    if(v<1){
      let s=parseFloat(v.toFixed(10)).toString(); const dec=s.split(".")[1]||"";
      let res="0."; let cnt=0;
      for(const c of dec){res+=c;if(c!=="0"&&++cnt===2)break;}
      return res.padEnd(res.length+Math.max(0,2-(res.split(".")[1]||"").length),"0");
    }
    return v.toFixed(1);
  };

  /* --------------- pointer helpers --------------- */
const ptrMove = (clientX, clientY, rect) => {
  const rel = clientX - rect.left - PADDING;
  if (rel < 0 || rel > EFFECTIVE_WIDTH) { setHoverIdx(null); return; }
  const idx = Math.round((rel / EFFECTIVE_WIDTH) * (processed.length-1));
  setHoverIdx(Math.max(0, Math.min(idx, processed.length-1)));
  setHoverY(clientY - rect.top);
};


  const opt = {
    platform : ["all","spotify","apple","amazon","spndat"],
    view     : ["stats","earnings"],
    metric   : ["streams","sales"],
    time     : ["week","month","3m"]
  };

  const [modal,setModal]=useState(false);

  /* --------------- UI --------------- */
  return (
    <div style={st.container}>
      {/* summary row */}
      <div style={st.summaryRow}>
      <span style={st.summaryTxt}>
  {platform === "all" ? "All Platforms" : platform}
  {" | "}
  {viewMode === "stats" ? "Stream Stats" : "Earnings"}
  {viewMode === "earnings" && (
    " | " + dataType.charAt(0).toUpperCase() + dataType.slice(1)
  )}
  {" | "}
  {timeframe === "3m" ? "3 Months" : timeframe.charAt(0).toUpperCase() + timeframe.slice(1)}
</span>

        <button style={st.filterBtn} onClick={()=>setModal(true)}>
          <IoFilter size={16} color="#fff" style={{marginRight:4}}/> Filter
        </button>
      </div>

      {/* chart */}
      <div style={st.chartWrap}>
        <svg
          width={SVG_WIDTH}
          height={chartHeight}
          viewBox={`0 0 ${SVG_WIDTH} ${chartHeight}`}
          onMouseMove={e=>ptrMove(e.clientX,e.clientY,e.currentTarget.getBoundingClientRect())}
          onMouseLeave={()=>{setHoverIdx(null); setHoverY(null);}}
          onMouseDown={e=>ptrMove(e.clientX,e.clientY,e.currentTarget.getBoundingClientRect())}
          onTouchStart={e=>ptrMove(e.touches[0].clientX,e.touches[0].clientY,e.currentTarget.getBoundingClientRect())}
          onTouchMove={e=>ptrMove(e.touches[0].clientX,e.touches[0].clientY,e.currentTarget.getBoundingClientRect())}
          onTouchEnd={()=>{setHoverIdx(null); setHoverY(null);}}
        >
          {/* gradient + line */}
          <defs>
            <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="#4F8EF7" stopOpacity="0.4"/>
              <stop offset="100%" stopColor="#4F8EF7" stopOpacity="0"/>
            </linearGradient>
          </defs>
          {fillPath && <path d={fillPath} fill="url(#grad)"/>}
          {linePath && <path d={linePath} stroke="#4F8EF7" strokeWidth="2" fill="none"/>}

          {/* dots + ALWAYS-VISIBLE value labels */}
          {processed.map((v,i)=>{
            const x=PADDING + (i/(processed.length-1))*EFFECTIVE_WIDTH;
            const y=PADDING + (EFFECTIVE_HEIGHT-(v/maxVal)*EFFECTIVE_HEIGHT);
            return (
              <g key={i}>
                <circle cx={x} cy={y} r="3" fill="#fff"/>
                <text
                  x={x}
                  y={y-10}
                  fontSize="10"
                  fontWeight="600"
                  fill="#fff"
                  textAnchor="middle"
                >
                  {fmtVal(v)}
                </text>
              </g>
            );
          })}

          {/* hover helper line + date label */}
          {hoverIdx!==null && (()=>{               // IIFE to compute once
            const x = PADDING + (hoverIdx/(processed.length-1))*EFFECTIVE_WIDTH;
            const anchor = x > SVG_WIDTH/2 ? "end" : "start";
            const labelX = x + (anchor==="end"? -8 : 8);
            const labelY = Math.max(12, Math.min(chartHeight - 8, (hoverY ?? 0) - 10));
            return (
              <>
                <line x1={x} y1={0} x2={x} y2={chartHeight}
                      stroke="#fff" strokeWidth="1" strokeDasharray="4"/>
<text
  x={labelX}
  y={labelY}                 // ← UPDATED
  fontSize="12"
  fontWeight="600"
  fill="#fff"
  textAnchor={anchor}
>
                  {labels[hoverIdx]}
                </text>
              </>
            );
          })()}
        </svg>
      </div>

      {/* x-axis labels */}
      {xTicks.length>0 && (
        <div style={st.xAxis}>
          {xTicks.map((t,i)=><span key={i} style={st.xTick}>{t}</span>)}
        </div>
      )}
            {modal && (
        <div style={st.modalOverlay} onClick={()=>setModal(false)}>
          <div style={st.modal} onClick={e=>e.stopPropagation()}>
            <h3 style={st.modalTitle}>Select Options</h3>

            <div style={st.optRow}>
  <span style={st.optTitle}>View</span>
  <div style={st.optScroll}>
    {[
      { key: "stats",   label: "Stream Stats"   },
      { key: "earnings", label: "Your Earnings" }
    ].map(o => (
      <button
        key={o.key}
        onClick={() => {
          setViewMode(o.key);
          //  every time you leave “earnings | sales” ensure the chart goes back to streams
          if (o.key === "stats" && dataType === "sales")  setDataType("streams");
        }}
        

        style={
          viewMode === o.key
            ? { ...st.optBtn, ...st.optBtnSel }
            : st.optBtn
        }
      >
        {o.label}
      </button>
    ))}
  </div>
</div>

            {viewMode==="stats" && (
   <OptRow title="Platform" list={opt.platform} value={platform} setter={setPlatform}/>
 )}
{/* inside your modal’s JSX */}
{viewMode === "earnings" && (
  <OptRow
  title="Metric"
  list={opt.metric}
  value={dataType}
  setter={setDataType}
/>

)}

            <OptRow title="Time"    list={opt.time}     value={timeframe} setter={setTimeframe}/>

            <button style={st.doneBtn} onClick={() => {
  setModal(false);
  if (viewMode === "stats" && dataType === "sales") setDataType("streams");
}}
>Done</button>
          </div>
        </div>
      )}
    </div>

  );
}

function OptRow({title,list,value,setter}){
  return (
    <div style={st.optRow}>
      <span style={st.optTitle}>{title}</span>
      <div style={st.optScroll}>
        {list.map(v=>(
          <button
            key={v}
            onClick={()=>setter(v)}
            style={v===value ? {...st.optBtn,...st.optBtnSel} : st.optBtn}
          >
            {v.charAt(0).toUpperCase()+v.slice(1)}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ───────── styles ───────── */
const st={
  container:{
    width:WIDTH,maxWidth:FRAME_WIDTH,margin:"0 auto",
    background:"#0e061e",borderRadius:12,padding:14,
    boxShadow:"0 0 8px rgba(0,0,0,0.25)",fontFamily:"brhendrix"
  },
  summaryRow:{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8},
  summaryTxt:{flex:1,color:"#fff",fontSize:Math.max(13,WIDTH*0.032),fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",marginRight:12},
  filterBtn:{flexShrink:0,display:"flex",alignItems:"center",border:"1px solid #4F8EF7",background:"transparent",color:"#fff",borderRadius:5,padding:"6px 10px",cursor:"pointer"},
  chartWrap:{width:"100%",overflowX:"hidden",display:"flex",justifyContent:"center",margin:"10px 0"},
  xAxis:{display:"flex",justifyContent:"space-between",padding:"0 14px",marginTop:8},
  xTick:{color:"#fff",fontSize:12},
  modalOverlay:{ position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",display:"flex",justifyContent:"center",alignItems:"center",zIndex:1000},
  modal:{height: '400px',marginBottom: '250px',width: '475px',background:"#0e061e",borderRadius:10,padding:16,},
  modalTitle:{color:"#fff",fontSize:18,textAlign:"center",marginBottom:12},
  optRow:{marginBottom:18},
  optTitle:{color:"#fff",fontSize:'18px',fontWeight:600,marginBottom:4},
  optScroll:{display:"flex",overflowX:"auto"},
  optBtn:{background:"#333",border:"none",borderRadius:8,color:"#fff",padding:"6px 10px",marginRight:8,marginBottom:8,cursor:"pointer",fontSize:'16px'},
  optBtnSel:{background:"#62fbfa",color:"#1E1E1E"},
  doneBtn:{fontSize: '18px',border:"1px solid #4F8EF7",borderRadius:5,padding:"8px 20px",background:"transparent",color:"#fff",cursor:"pointer",display:"block",margin:"10px auto 0"},
};
